import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getAuditLogsRequest, exportAuditLogsRequest } from '../../../../redux/modules/auditLog/slice';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, TextField, FormControl, InputLabel, Select, MenuItem, Button, IconButton,
    CircularProgress, TablePagination, Collapse, Tooltip, Grid, Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white, fontWeight: 600 },
    [`&.${tableCellClasses.body}`]: { fontSize: 13 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:last-child td, &:last-child th': { border: 0 },
}));

const EVENT_TYPES = [
    { value: '', label: 'All Events' },
    { value: 'AUTH_LOGIN_SUCCESS', label: 'Login Success' },
    { value: 'AUTH_LOGIN_FAILED', label: 'Login Failed' },
    { value: 'AUTH_LOGOUT', label: 'Logout' },
    { value: 'AUTH_PASSWORD_RESET_REQUEST', label: 'Password Reset Request' },
    { value: 'AUTH_PASSWORD_CHANGED', label: 'Password Changed' },
    { value: 'PHI_ACCESS', label: 'PHI Access' },
    { value: 'PHI_CREATE', label: 'PHI Create' },
    { value: 'PHI_UPDATE', label: 'PHI Update' },
    { value: 'PHI_DELETE', label: 'PHI Delete' },
    { value: 'PHI_EXPORT', label: 'PHI Export' },
    { value: 'ADMIN_USER_CREATE', label: 'Admin: User Create' },
    { value: 'ADMIN_USER_UPDATE', label: 'Admin: User Update' },
    { value: 'ADMIN_USER_DELETE', label: 'Admin: User Delete' },
    { value: 'ADMIN_SETTINGS_CHANGE', label: 'Admin: Settings Change' },
    { value: 'REPORT_GENERATED', label: 'Report Generated' },
    { value: 'REPORT_VIEWED', label: 'Report Viewed' },
    { value: 'SECURITY_RATE_LIMIT_EXCEEDED', label: 'Rate Limit Exceeded' },
    { value: 'SECURITY_INVALID_TOKEN', label: 'Invalid Token' },
    { value: 'SECURITY_UNAUTHORIZED_ACCESS', label: 'Unauthorized Access' },
];

const RESOURCE_TYPES = [
    { value: '', label: 'All Resources' },
    { value: 'Patient', label: 'Patient' },
    { value: 'Order', label: 'Order' },
    { value: 'Report', label: 'Report' },
    { value: 'User', label: 'User' },
    { value: 'Appointment', label: 'Appointment' },
    { value: 'DicomStudy', label: 'DICOM Study' },
];

const eventTypeChipColor = (eventType) => {
    if (!eventType) return 'default';
    if (eventType.startsWith('PHI_')) return 'info';
    if (eventType.startsWith('AUTH_')) return 'secondary';
    if (eventType.startsWith('ADMIN_')) return 'warning';
    if (eventType.startsWith('REPORT_')) return 'success';
    if (eventType.startsWith('SECURITY_')) return 'error';
    return 'default';
};

const eventTypeLabel = (eventType) => {
    const found = EVENT_TYPES.find(e => e.value === eventType);
    return found ? found.label : eventType;
};

const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

function AuditLogRow({ log }) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <StyledTableRow sx={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
                <StyledTableCell sx={{ width: 30, p: 0.5 }}>
                    <IconButton size="small">
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </StyledTableCell>
                <StyledTableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(log.timestamp)}</StyledTableCell>
                <StyledTableCell>
                    <Chip
                        label={eventTypeLabel(log.eventType)}
                        color={eventTypeChipColor(log.eventType)}
                        size="small"
                        sx={{ fontWeight: 500, fontSize: 11 }}
                    />
                </StyledTableCell>
                <StyledTableCell>{log.userEmail || '-'}</StyledTableCell>
                <StyledTableCell>{log.resourceType || '-'}</StyledTableCell>
                <StyledTableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.resourceId || '-'}
                </StyledTableCell>
                <StyledTableCell>{log.action || '-'}</StyledTableCell>
                <StyledTableCell align="center">
                    {log.success ?
                        <Tooltip title="Success"><CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} /></Tooltip> :
                        <Tooltip title={log.failureReason || 'Failed'}><ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} /></Tooltip>
                    }
                </StyledTableCell>
            </StyledTableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2, p: 2, borderRadius: 1, backgroundColor: 'grey.50' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">User Role</Typography>
                                    <Typography variant="body2">{log.userRole || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">IP Address</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{log.ipAddress || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Endpoint</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{log.endpoint || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Status Code</Typography>
                                    <Typography variant="body2">{log.statusCode || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Patient ID</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{log.patientId || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Branch ID</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{log.branchId || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Request ID</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{log.requestId || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Response Time</Typography>
                                    <Typography variant="body2">{log.metadata?.responseTime ? `${log.metadata.responseTime}ms` : '-'}</Typography>
                                </Grid>
                                {log.failureReason && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" display="block">Failure Reason</Typography>
                                        <Typography variant="body2" color="error">{log.failureReason}</Typography>
                                    </Grid>
                                )}
                                {log.userAgent && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" display="block">User Agent</Typography>
                                        <Typography variant="body2" sx={{ fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.secondary' }}>{log.userAgent}</Typography>
                                    </Grid>
                                )}
                                {log.changes && (log.changes.before || log.changes.after) && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" display="block">Changes</Typography>
                                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                            {log.changes.before && (
                                                <Box sx={{ flex: 1, p: 1, backgroundColor: 'error.light', borderRadius: 1, opacity: 0.7 }}>
                                                    <Typography variant="caption" fontWeight={600}>Before</Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap' }}>{JSON.stringify(log.changes.before, null, 2)}</Typography>
                                                </Box>
                                            )}
                                            {log.changes.after && (
                                                <Box sx={{ flex: 1, p: 1, backgroundColor: 'success.light', borderRadius: 1, opacity: 0.7 }}>
                                                    <Typography variant="caption" fontWeight={600}>After</Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap' }}>{JSON.stringify(log.changes.after, null, 2)}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function LabAuditTrail() {
    const dispatch = useDispatch();
    const { getRes, loading } = useSelector(state => state.auditLog);

    const [eventType, setEventType] = useState('');
    const [resourceType, setResourceType] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        dispatch(setMainHeading("Audit Trail"));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getAuditLogsRequest({
            page: page + 1,
            limit: rowsPerPage,
            eventType,
            resourceType,
            dateFrom,
            dateTo,
        }));
    }, [dispatch, page, rowsPerPage]);

    const handleSearch = () => {
        setPage(0);
        dispatch(getAuditLogsRequest({
            page: 1,
            limit: rowsPerPage,
            eventType,
            resourceType,
            dateFrom,
            dateTo,
        }));
    };

    const handleClear = () => {
        setEventType('');
        setResourceType('');
        setDateFrom('');
        setDateTo('');
        setPage(0);
        dispatch(getAuditLogsRequest({ page: 1, limit: rowsPerPage }));
    };

    const handleExport = () => {
        dispatch(exportAuditLogsRequest({ eventType, resourceType, dateFrom, dateTo }));
    };

    const logs = getRes?.data?.logs || [];
    const pagination = getRes?.data?.pagination || { total: 0, pages: 0 };

    return (
        <Box sx={{ p: 3 }}>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Event Type</InputLabel>
                            <Select value={eventType} label="Event Type" onChange={e => setEventType(e.target.value)}>
                                {EVENT_TYPES.map(et => (
                                    <MenuItem key={et.value} value={et.value}>{et.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Resource Type</InputLabel>
                            <Select value={resourceType} label="Resource Type" onChange={e => setResourceType(e.target.value)}>
                                {RESOURCE_TYPES.map(rt => (
                                    <MenuItem key={rt.value} value={rt.value}>{rt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth size="small" type="date" label="From"
                            InputLabelProps={{ shrink: true }}
                            value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth size="small" type="date" label="To"
                            InputLabelProps={{ shrink: true }}
                            value={dateTo} onChange={e => setDateTo(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3.5}>
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} size="small">
                                Search
                            </Button>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClear} size="small">
                                Clear
                            </Button>
                            <Button variant="outlined" color="success" startIcon={<FileDownloadOutlinedIcon />} onClick={handleExport} size="small">
                                Export CSV
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Results Table */}
            <Paper>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell sx={{ width: 30 }} />
                                        <StyledTableCell>Timestamp</StyledTableCell>
                                        <StyledTableCell>Event</StyledTableCell>
                                        <StyledTableCell>User</StyledTableCell>
                                        <StyledTableCell>Resource</StyledTableCell>
                                        <StyledTableCell>Resource ID</StyledTableCell>
                                        <StyledTableCell>Action</StyledTableCell>
                                        <StyledTableCell align="center">Status</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No audit logs found. Adjust your filters and try again.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        logs.map((log, idx) => (
                                            <AuditLogRow key={log._id || idx} log={log} />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={pagination.total}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );
}
